/**
 * AI Product Manager - Intent Analyzer (Section 1)
 * Classifies product type and domain from user prompts.
 */

import { aiRouter } from "@/ai/providers/aiRouter";

export type ProductType = "saas" | "website" | "internal_tool" | "marketplace" | "mobile_app" | "ecommerce";
export type ProductDomain = string;

export interface ProductIntent {
  productType: ProductType;
  domain: string;
  confidence: number;
  keywords: string[];
}

const TYPE_PATTERNS: { type: ProductType; patterns: RegExp; weight: number }[] = [
  { type: "saas", patterns: /saas|subscription|billing|plans?|pricing|tenant|multi-?tenant/i, weight: 1 },
  { type: "marketplace", patterns: /marketplace|listing|seller|buyer|vendor|two-?sided/i, weight: 1 },
  { type: "ecommerce", patterns: /e-?commerce|shop|store|cart|checkout|product catalog/i, weight: 1 },
  { type: "internal_tool", patterns: /internal\s?tool|admin\s?panel|back\s?office|operations|team\s?tool|\bcrm\b|contact\s?manag|relationship\s?manag/i, weight: 1 },
  { type: "mobile_app", patterns: /mobile\s?app|ios|android|native|react\s?native|capacitor/i, weight: 1 },
  { type: "website", patterns: /website|landing|portfolio|blog|brochure|static/i, weight: 0.8 },
];

const DOMAIN_PATTERNS: { domain: string; patterns: RegExp }[] = [
  { domain: "CRM", patterns: /\bcrm\b|sales\s?crm|lead\s?manag|pipeline|deal\s?manag|contact\s?manag|relationship\s?manag/i },
  { domain: "Hospitality", patterns: /hotel|hostel|resort|lodging|guesthouse|hospitality|bed\s?and\s?breakfast/i },
  { domain: "Restaurant", patterns: /restaurant|cafe|diner|bistro|food\s?order|point\s?of\s?sale|\bpos\b/i },
  { domain: "Faith & Nonprofit", patterns: /church|congregation|ministry|nonprofit|non-?profit|charity|donor/i },
  { domain: "Legal", patterns: /legal|law\s?firm|attorney|litigation|paralegal/i },
  { domain: "Logistics", patterns: /logistics|shipping|delivery|fleet|freight|courier/i },
  { domain: "Banking", patterns: /\bbank(?:ing)?\b|fintech|neobank/i },
  { domain: "Events", patterns: /event\s?booking|event\s?manage|ticketing|venue|conference|\bevents?\b/i },
  { domain: "ERP & Operations", patterns: /\berp\b|enterprise\s?resource|supply\s?chain|procurement/i },
  { domain: "Marketplace", patterns: /marketplace|two-?sided/i },
  { domain: "Support CRM", patterns: /support|ticket|helpdesk|customer\s?service/i },
  { domain: "Customer Success", patterns: /customer\s?success|churn|health\s?score|onboarding/i },
  { domain: "Project Management", patterns: /project\s?manage|task|kanban|sprint|agile|scrum/i },
  { domain: "HR & People", patterns: /\bhr\b|human\s?resource|employee|hiring|recruitment|payroll/i },
  { domain: "Finance", patterns: /finance|accounting|invoice|expense|budget/i },
  { domain: "Healthcare", patterns: /health|hospital|medical|patient|clinic|appointment|ehr/i },
  { domain: "Education", patterns: /education|learn|course|student|lms|school/i },
  { domain: "Real Estate", patterns: /real\s?estate|property|listing|agent|tenant|landlord/i },
  { domain: "Fitness & Wellness", patterns: /fitness|wellness|gym|workout|nutrition|health\s?track/i },
  { domain: "E-commerce", patterns: /e-?commerce|shop|store|product|inventory/i },
  { domain: "Social & Community", patterns: /social|community|forum|chat|messaging/i },
  { domain: "Analytics", patterns: /analytics|dashboard|metrics|reporting|data\s?viz/i },
  { domain: "Content Management", patterns: /cms|blog|content|article|editorial/i },
  { domain: "General", patterns: /.*/i },
];

/**
 * Analyze a user prompt and classify product intent.
 */
export function analyzeProductIntent(prompt: string): ProductIntent {
  const lower = prompt.toLowerCase();
  const keywords: string[] = [];

  // Detect product type
  let bestType: ProductType = "saas";
  let bestWeight = 0;
  for (const { type, patterns, weight } of TYPE_PATTERNS) {
    const match = lower.match(patterns);
    if (match && weight > bestWeight) {
      bestType = type;
      bestWeight = weight;
      keywords.push(match[0]);
    }
  }

  // Detect domain
  let domain = "General";
  for (const { domain: d, patterns } of DOMAIN_PATTERNS) {
    if (d === "General") continue;
    const match = lower.match(patterns);
    if (match) {
      domain = d;
      keywords.push(match[0]);
      break;
    }
  }

  const confidence = Math.min(0.5 + keywords.length * 0.15, 0.95);

  return { productType: bestType, domain, confidence, keywords };
}

/**
 * LLM-required intent analysis.
 * This function is used by LLM-first build pipelines and therefore MUST NOT
 * silently downgrade to heuristic classification. The synchronous
 * analyzeProductIntent() remains available for explicitly deterministic callers.
 */
export async function analyzeProductIntentWithLLM(prompt: string): Promise<ProductIntent> {
  const result = await aiRouter.generate({
    prompt: `Classify this product idea: "${prompt}"\\n\\nRespond with ONLY valid JSON: {"productType":"saas|website|internal_tool|marketplace|mobile_app|ecommerce","domain":"string","confidence":0.85,"keywords":["string"]}`,
    projectId: "intent-analysis",
    systemPrompt: `You are a product classifier. Product types: saas (subscription/multi-tenant apps), website (static/marketing/landing pages), internal_tool (ops/admin/back-office tools), marketplace (two-sided platforms with buyers and sellers), mobile_app (iOS/Android/React Native apps), ecommerce (stores/carts/checkouts). Domain examples: CRM, Project Management, Healthcare, Education, Finance, HR & People, Fitness & Wellness, E-commerce, Real Estate, Social & Community, Analytics, Content Management, General. Return ONLY JSON, no markdown.`,
    maxTokens: 150,
  });

  if (result.error || !result.raw) {
    throw new Error(
      `AI_INTENT_ANALYSIS_FAILED: ${result.error?.message ?? "The AI provider returned no usable intent output."}`,
    );
  }

  const match = result.raw.trim().match(/\{[\s\S]*\}/);
  if (!match) {
    throw new Error("AI_INTENT_ANALYSIS_INVALID_OUTPUT: The AI provider did not return a JSON intent object.");
  }

  let parsed: Partial<ProductIntent>;
  try {
    parsed = JSON.parse(match[0]) as Partial<ProductIntent>;
  } catch {
    throw new Error("AI_INTENT_ANALYSIS_INVALID_OUTPUT: The AI provider returned invalid JSON.");
  }

  if (!parsed.productType || !parsed.domain) {
    throw new Error("AI_INTENT_ANALYSIS_INVALID_OUTPUT: Required productType/domain fields are missing.");
  }

  const validTypes: ProductType[] = ["saas", "website", "internal_tool", "marketplace", "mobile_app", "ecommerce"];
  if (!validTypes.includes(parsed.productType as ProductType)) {
    throw new Error(`AI_INTENT_ANALYSIS_INVALID_OUTPUT: Unsupported product type "${String(parsed.productType)}".`);
  }

  return {
    productType: parsed.productType as ProductType,
    domain: parsed.domain,
    confidence: typeof parsed.confidence === "number" ? Math.min(Math.max(parsed.confidence, 0), 0.99) : 0.85,
    keywords: Array.isArray(parsed.keywords) ? parsed.keywords.map(String).filter(Boolean) : [],
  };
}

export function productTypeLabel(type: ProductType): string {
  const labels: Record<ProductType, string> = {
    saas: "SaaS Application",
    website: "Website",
    internal_tool: "Internal Tool",
    marketplace: "Marketplace Platform",
    mobile_app: "Mobile Application",
    ecommerce: "E-Commerce Store",
  };
  return labels[type];
}
